import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { Logger } from '@suntex.core/logging';
import { AuthError, AuthService } from '@suntex.fim/auth';
import { LoadingSpinner } from '@suntex.fim/ui';

import { useHomepageRedirect } from '../hooks/useHomepageRedirect';

const logger = Logger.for(['JwtUrlLogin']);

function hasJwtParam(): boolean {
  return new URLSearchParams(window.location.search).has('jwt');
}

/**
 * Builds a user/support-facing message from a JWT sign-in failure, including
 * the underlying cause (e.g. a network timeout or server error message) so
 * the error page gives enough detail to diagnose the problem without needing
 * to dig through logs.
 */
function describeSignInError(e: unknown): { message: string; code?: string } {
  const message =
    e instanceof AuthError ? e.message : 'Failed to authenticate with JWT';
  const code = e instanceof AuthError ? e.code : undefined;
  const originalError = e instanceof AuthError ? e.originalError : undefined;
  const detail =
    originalError instanceof Error ? originalError.message : undefined;
  return { message: detail ? `${message}: ${detail}` : message, code };
}

function redirectToAuthError(
  errorPath: string,
  { message, code }: { message: string; code?: string },
): void {
  const params = new URLSearchParams();
  params.set('message', message);
  if (code) params.set('code', code);
  window.location.assign(`${errorPath}?${params.toString()}`);
}

/**
 * Handles redirect-based sign-on from the classic site.
 * When a `?jwt=<token>` query parameter is present, authenticates the user
 * using the JWT and immediately removes the parameter from the URL to prevent
 * it from persisting in the browser history.
 *
 * Gates rendering of `children` until any pending URL-JWT sign-in resolves,
 * so a stale, already-authenticated session cannot briefly render (and
 * navigate away with) the wrong user before the new session takes over.
 *
 * If sign-in fails, redirects to `errorPath` with a `message` (including the
 * underlying cause when available) and `code` query param, so failures are
 * diagnosable instead of silently falling back to a normal, unauthenticated
 * page load.
 *
 * Rendered internally by `FrameworkProvider`.
 */
export function JwtUrlLogin({
  authService,
  children,
  errorPath = '/auth-error',
}: {
  authService: AuthService;
  children: ReactNode;
  /** Path to redirect to when the URL-JWT sign-in fails. Defaults to '/auth-error' */
  errorPath?: string;
}): ReactNode {
  const [hadJwtParam] = useState(hasJwtParam);
  const [pending, setPending] = useState(hadJwtParam);
  const hasRun = useRef(false);
  const { setRedirectEnabled } = useHomepageRedirect();

  useLayoutEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.search);
    const jwt = params.get('jwt');
    if (jwt === null) return;

    setRedirectEnabled(false);

    // Remove the JWT from the URL before initiating auth so it cannot be
    // bookmarked or captured in server-side logs after this point.
    params.delete('jwt');
    const newSearch = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname +
        (newSearch ? `?${newSearch}` : '') +
        window.location.hash,
    );

    authService
      .signIn({ jwt })
      .then(() => setPending(false))
      .catch((e: unknown) => {
        logger.warn(
          'Failed to sign in from JWT URL. Redirecting to the auth error page.',
          { jwt: { length: jwt.length } },
          e,
        );
        redirectToAuthError(errorPath, describeSignInError(e));
        setRedirectEnabled(true);
        // Leave `pending` (and the loading spinner) in place while the
        // browser navigates away, rather than briefly rendering `children`
        // in a stale/unauthenticated state first.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (pending) return <LoadingSpinner />;
  return children;
}
